import type { ExcelExchangeManifest, ExcelImportJobRow, ExcelImportMode } from '@tcr-erp/shared';
import api from './api';

export type ExcelImportEntity = string;
export type ExcelExportMode = 'data' | 'print_template' | 'print_pdf';
export type ExcelExportStrategy = 'sync' | 'paged' | 'async_job';

export interface ExcelImportUploadSheet {
  index: number;
  name: string;
  row_count: number;
}

export type ExcelPreviewCell = string | number | boolean | null;
export type ExcelPreviewRow = ExcelPreviewCell[];

export interface ExcelImportUploadResponse {
  upload_id: string;
  columns: string[];
  preview: ExcelPreviewRow[];
  preview_rows: ExcelPreviewRow[];
  total_rows: number;
  file_name: string;
  file_size: number;
  parser_type: string;
  sheets: ExcelImportUploadSheet[];
  sheet_index: number;
  header_row: number;
}

export interface ExcelAnalyzeImportPayload {
  upload_id: string;
  sheet_index?: number;
  header_row?: number;
}

export interface ExcelAnalyzeImportResponse {
  upload_id: string;
  columns: string[];
  preview_rows: ExcelPreviewRow[];
  total_rows: number;
  sheet_index: number;
  header_row: number;
  file_name: string;
  file_size: number;
  parser_type: string;
  sheets: ExcelImportUploadSheet[];
}

export interface ExcelValidateImportPayload extends ExcelAnalyzeImportPayload {
  entity: ExcelImportEntity;
  mapping?: Record<string, string>;
  mode?: ExcelImportMode;
  write_mode?: 'create_only' | 'skip_existing' | 'update_existing' | 'upsert' | 'replace_mapped_fields';
  auto_commit?: boolean;
  allow_partial_commit?: boolean;
  store_id?: string;
}

export interface ExcelExportJobRequest {
  entity: string;
  format?: 'xlsx' | 'csv' | 'json';
  export_mode: ExcelExportMode;
  export_strategy: ExcelExportStrategy;
  filters: Record<string, string | number | boolean | undefined>;
  field_keys?: string[];
  chunk_options?: {
    page_size: number;
    max_rows_per_sheet: number;
  };
  print_options?: {
    repeat_header: boolean;
    split_group_to_files: boolean;
    show_row_number: boolean;
    show_carry_forward: boolean;
    paper_size: 'A4' | 'A3' | 'K80';
    orientation: 'portrait' | 'landscape';
    signer_ids?: string[];
    font_family?: string;
  };
}

// TCR-2500 — tiến trình thật của job export do worker ghi (hợp đồng 3 repo).
// Field tuỳ chọn: job cũ tạo trước TCR-2500 không có → undefined/null.
export interface ExcelExportJobProgress {
  phase?: 'collecting' | 'building' | 'uploading';
  processed?: number;
  total?: number | null;
  percent?: number;
  updated_at?: string;
}

export interface ExcelExportJobResponse {
  export_id: string;
  status?: 'pending' | 'processing' | 'completed' | 'failed';
  download_url?: string | null;
  // TCR-2500 — progress nguyên văn từ BE; error = nguyên nhân thất bại từ BE
  // (trước đây FE chỉ hiện thông báo chung, không có lý do cụ thể).
  progress?: ExcelExportJobProgress | null;
  error?: string | null;
}

export interface ExcelImportJobStatus {
  import_id: string;
  tenant_id: string;
  upload_id: string | null;
  entity: ExcelImportEntity;
  file_name: string;
  duplicate_action: 'skip' | 'upsert' | 'create';
  write_mode?: 'create_only' | 'skip_existing' | 'update_existing' | 'upsert' | 'replace_mapped_fields';
  mapping: Record<string, string>;
  status: 'pending' | 'processing' | 'awaiting_confirmation' | 'completed' | 'failed';
  phase?: 'queued' | 'validating' | 'waiting_confirmation' | 'committing' | 'completed' | 'failed' | string;
  progress: number;
  total_rows: number;
  processed_rows: number;
  success_rows: number;
  warning_rows: number;
  error_rows: number;
  parser_type: string | null;
  memory_peak_mb: number;
  error: string | null;
  created_by: string | null;
  is_dryrun: boolean;
  auto_commit?: boolean;
  allow_partial_commit?: boolean;
  action_summary: {
    create_rows: number;
    update_rows: number;
    skip_rows: number;
    warning_rows: number;
    error_rows: number;
  } | null;
  created_at?: string;
  started_at: string | null;
  completed_at: string | null;
  awaiting_confirmation_at: string | null;
  confirmed_at: string | null;
  validation_result: Record<string, unknown> | null;
}

export interface ExcelImportHistoryResponse {
  data: Array<ExcelImportJobStatus & {
    actor?: {
      id: string | null;
      name: string | null;
      email: string | null;
    };
  }>;
  total: number;
  page: number;
  page_size: number;
}

export interface ExcelImportJobRowsQuery {
  page?: number;
  page_size?: number;
  status?: 'all' | 'valid' | 'warning' | 'error';
}

export interface ExcelImportJobRowItem extends ExcelImportJobRow {
  issues: Array<{
    row: number;
    column: string;
    message: string;
    severity: 'error' | 'warning';
  }>;
  source_data: Record<string, unknown>;
}

export interface ExcelImportJobRowsResponse {
  data: ExcelImportJobRowItem[];
  total: number;
  page: number;
  page_size: number;
  summary: {
    valid: number;
    warning: number;
    error: number;
  };
}

export const excelService = {
  async getManifest(entity: string): Promise<ExcelExchangeManifest> {
    const response = await api.get<ExcelExchangeManifest>(`/excel/manifests/${entity}`);
    return response.data;
  },

  async uploadImportFile(file: File, entity?: string): Promise<ExcelImportUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (entity) formData.append('entity', entity);
    const response = await api.post<ExcelImportUploadResponse>('/excel/import/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async analyzeImport(payload: ExcelAnalyzeImportPayload): Promise<ExcelAnalyzeImportResponse> {
    const response = await api.post<ExcelAnalyzeImportResponse>('/excel/import/analyze', payload);
    return response.data;
  },

  async validateImport(payload: ExcelValidateImportPayload): Promise<ExcelImportJobStatus> {
    const response = await api.post<ExcelImportJobStatus>('/excel/import/validate', payload);
    return response.data;
  },

  async getImportRows(id: string, params: ExcelImportJobRowsQuery = {}): Promise<ExcelImportJobRowsResponse> {
    const response = await api.get<ExcelImportJobRowsResponse>(`/excel/import-jobs/${id}/rows`, { params });
    return response.data;
  },

  async getImportStatus(id: string): Promise<ExcelImportJobStatus> {
    const response = await api.get<ExcelImportJobStatus>(`/import/jobs/${id}/status`);
    return response.data;
  },

  async getImportHistory(params: { entity?: string; page?: number; page_size?: number; status?: string } = {}): Promise<ExcelImportHistoryResponse> {
    const response = await api.get<ExcelImportHistoryResponse>('/import/history', { params });
    return response.data;
  },

  async deleteImportJob(id: string): Promise<{ success: boolean }> {
    const response = await api.delete<{ success: boolean }>(`/import/jobs/${id}`);
    return response.data;
  },

  async commitImport(id: string): Promise<ExcelImportJobStatus> {
    const response = await api.post<ExcelImportJobStatus>(`/excel/import-jobs/${id}/commit`);
    return response.data;
  },

  async downloadImportErrorFile(id: string): Promise<Blob> {
    const response = await api.get(`/excel/import-jobs/${id}/error-file`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  async createExportJob(payload: ExcelExportJobRequest): Promise<ExcelExportJobResponse> {
    const response = await api.post<ExcelExportJobResponse>('/excel/export-jobs', {
      format: payload.format ?? 'xlsx',
      ...payload,
    });
    return response.data;
  },

  async getExportJob(id: string): Promise<ExcelExportJobResponse> {
    const response = await api.get<ExcelExportJobResponse>(`/excel/export-jobs/${id}`);
    return response.data;
  },

  async downloadExportFile(id: string): Promise<{ blob: Blob; filename: string }> {
    const response = await api.get<Blob>(`/excel/export-jobs/${id}/download`, {
      responseType: 'blob',
    });
    return {
      blob: response.data as Blob,
      filename: getFileNameFromContentDisposition(response.headers['content-disposition']) || `export-${id}.xlsx`,
    };
  },

};

export type { ExcelExchangeManifest };

function getFileNameFromContentDisposition(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const plainMatch = value.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] ?? null;
}
